import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import type { CategoryDetailOut } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { StatCell } from '@/components/common/show/StatCell';
import { CATEGORY_SHOW_TABS } from '../constants';
import type { CategoryShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<CategoryShowTab, string> = {
  general: 'base.sections.basic_info',
  publications: 'user_catalog.common.tabs.publications',
  collection: 'base.sections.collection_info',
  search: 'user_catalog.common.tabs.search',
};

interface CategoryShowHeaderProps {
  record: CategoryDetailOut;
  activeTab: CategoryShowTab;
  onTabChange: (key: string) => void;
}

export const CategoryShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: CategoryShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.image_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Title>
            {record.is_system && (
              <Tag color="gold">
                {t('user_catalog.categories.fields.is_system')}
              </Tag>
            )}
            {record.is_featured && (
              <Tag color="blue">
                {t('user_catalog.categories.fields.is_featured')}
              </Tag>
            )}
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            {record.parent_id != null && (
              <StatCell label={t('user_catalog.categories.fields.parent_id')}>
                <ShowIconLink
                  resource="user_catalog.categories"
                  id={record.parent_id}
                >
                  {record.parent_id}
                </ShowIconLink>
              </StatCell>
            )}
            <StatCell label={t('user_catalog.categories.fields.position')}>
              {record.position}
            </StatCell>
            <StatCell label={t('user_catalog.categories.sections.title')}>
              {record.sections?.length ?? 0}
            </StatCell>
            <StatCell label={t('base.fields.mod_status')}>
              <ModStatusTag value={record.mod_status} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={CATEGORY_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
